import * as React from 'react';
import { Search } from 'lucide-react';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  registeredFieldSelectionId,
  type SpreadsheetIntentCandidate,
  type SpreadsheetMappingReviewSelection,
  type SpreadsheetIntentResolution,
} from '../../../domains/knowledge-ingestion/services/SpreadsheetIntentResolver';

const UNMAPPED_VALUE = '__unmapped__';

type MappingFilter = 'all' | 'attention' | 'mapped' | 'ignored';

interface MappingRow {
  readonly id: string;
  readonly label: string;
  readonly required: boolean;
  readonly kind: 'semantic' | 'registered';
}

export interface SourceIntentReviewPanelProps {
  readonly resolution: SpreadsheetIntentResolution;
  readonly selectedCandidate: SpreadsheetIntentCandidate;
  readonly selectedIntent: string | null;
  readonly headers: readonly string[];
  readonly sampleRows: readonly Record<string, unknown>[];
  readonly mappingSelections: SpreadsheetMappingReviewSelection;
  /**
   * Rows pre-filled by the column suggester rather than by pattern matching.
   * Marked in the UI so a suggestion is never mistaken for a confident match.
   */
  readonly assistedRoleIds?: readonly string[];
  readonly rowCount: number;
  readonly columnCount: number;
  readonly attentionRequestKey?: number;
  readonly onSelectIntent: (intent: string) => void;
  readonly onMappingChange: (fieldId: string, sourceField: string | null) => void;
}

export const SourceIntentReviewPanel: React.FC<SourceIntentReviewPanelProps> = ({
  resolution,
  selectedCandidate,
  selectedIntent,
  headers,
  sampleRows,
  mappingSelections,
  assistedRoleIds,
  rowCount,
  columnCount,
  attentionRequestKey,
  onSelectIntent,
  onMappingChange,
}) => {
  const [query, setQuery] = React.useState('');
  const [filter, setFilter] = React.useState<MappingFilter>('all');
  const mappingViewportRef = React.useRef<HTMLDivElement | null>(null);
  const handledAttentionRequestRef = React.useRef<number | undefined>(undefined);
  const semanticIds = React.useMemo(
    () => new Set(selectedCandidate.reviewFields.map((field) => field.id)),
    [selectedCandidate.reviewFields],
  );
  const registeredTargetFields = React.useMemo(() => {
    const targets = new Set(
      selectedCandidate.registeredFields.map((field) => field.targetField),
    );
    selectedCandidate.availableRegisteredFields.forEach((targetField) => {
      if (mappingSelections[registeredFieldSelectionId(targetField)]) {
        targets.add(targetField);
      }
    });
    return [...targets].filter((target) => !semanticIds.has(target)).sort();
  }, [mappingSelections, selectedCandidate, semanticIds]);
  const mappingRows = React.useMemo<MappingRow[]>(() => [
    ...selectedCandidate.reviewFields.map((field) => ({
      id: field.id,
      label: field.label,
      required: field.required,
      kind: 'semantic' as const,
    })),
    ...registeredTargetFields.map((targetField) => ({
      id: registeredFieldSelectionId(targetField),
      label: registeredFieldLabel(targetField, selectedCandidate.registeredFieldEntityRefs),
      required: false,
      kind: 'registered' as const,
    })),
  ], [registeredTargetFields, selectedCandidate.registeredFieldEntityRefs, selectedCandidate.reviewFields]);

  const headerOptions = React.useMemo<ComboboxOption[]>(() => [
    { value: UNMAPPED_VALUE, label: '— Not mapped —' },
    ...headers.map((header) => ({ value: header, label: header })),
  ], [headers]);
  const targetOptions = React.useMemo<ComboboxOption[]>(() => [
    { value: UNMAPPED_VALUE, label: '— Ignore this column —' },
    ...selectedCandidate.availableRegisteredFields.map((targetField) => ({
      value: targetField,
      label: `${registeredFieldLabel(targetField, selectedCandidate.registeredFieldEntityRefs)} · ${targetField}`,
    })),
  ], [selectedCandidate.availableRegisteredFields, selectedCandidate.registeredFieldEntityRefs]);

  const mappedUnknownTarget = React.useCallback((sourceField: string): string | null => {
    for (const targetField of selectedCandidate.availableRegisteredFields) {
      if (mappingSelections[registeredFieldSelectionId(targetField)] === sourceField) {
        return targetField;
      }
    }
    return null;
  }, [mappingSelections, selectedCandidate.availableRegisteredFields]);

  const missingRequired = mappingRows.filter(
    (row) => row.required && !mappingSelections[row.id],
  );
  const mappedCount = mappingRows.filter((row) => mappingSelections[row.id]).length;
  const ignoredHeaders = selectedCandidate.unrecognizedHeaders.filter(
    (header) => !mappedUnknownTarget(header),
  );
  const attentionCount = missingRequired.length;
  const normalizedQuery = query.trim().toLowerCase();
  const visibleMappingRows = mappingRows.filter((row) => {
    const selectedSource = mappingSelections[row.id] ?? null;
    const examples = examplesFor(sampleRows, selectedSource);
    if (filter === 'attention' && !(row.required && !selectedSource)) return false;
    if (filter === 'mapped' && !selectedSource) return false;
    if (filter === 'ignored') return false;
    return matchesQuery(normalizedQuery, [row.label, row.id, selectedSource, ...examples]);
  });
  const visibleUnknownHeaders = selectedCandidate.unrecognizedHeaders.filter((header) => {
    const target = mappedUnknownTarget(header);
    if (filter === 'mapped' && !target) return false;
    if (filter === 'ignored' && target) return false;
    if (filter === 'attention' && target) return false;
    return matchesQuery(normalizedQuery, [header, target]);
  });

  React.useEffect(() => {
    if (
      attentionRequestKey === undefined ||
      attentionRequestKey <= 0 ||
      handledAttentionRequestRef.current === attentionRequestKey
    ) return;
    handledAttentionRequestRef.current = attentionRequestKey;
    setQuery('');
    setFilter('attention');
    mappingViewportRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [attentionRequestKey]);

  return (
    <div
      className="space-y-3"
      data-testid="tid.compass.sourceIntentReview"
    >
      <section className="rounded-lg border border-border bg-background p-3">
        {/* The confidence badge that used to sit here repeated the percentage
            already printed on the selected card, a few pixels away. */}
        <div className="min-w-0">
          <div className="text-sm font-semibold text-foreground">
            What is this data for?
          </div>
          <div className="text-xs text-muted-foreground">
            {rowCount.toLocaleString()} row{rowCount === 1 ? '' : 's'} ·{' '}
            {columnCount.toLocaleString()} column{columnCount === 1 ? '' : 's'} in your file
          </div>
        </div>
        <div className="mt-3 grid gap-2 md:grid-cols-3">
          {resolution.candidates.map((candidate) => {
            const isSelected = candidate.intent === selectedIntent;
            return (
              <button
                key={candidate.intent}
                type="button"
                onClick={() => onSelectIntent(candidate.intent)}
                className={cn(
                  'rounded-md border px-3 py-2 text-left transition-colors',
                  isSelected
                    ? 'border-primary bg-[color:var(--primary)]/5'
                    : 'border-border bg-[color:var(--accent)]/25 hover:border-primary/40',
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  {/* Two lines, not an ellipsis: these labels ("Person
                      hierarchy with derived positions") are the whole basis
                      for the choice and truncating them hid the distinction. */}
                  <span className="line-clamp-2 text-sm font-medium text-foreground" title={candidate.label}>
                    {candidate.label}
                  </span>
                  <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                    {Math.round(candidate.confidence * 100)}%
                  </span>
                </div>
                <div className="mt-1 truncate text-[11px] text-muted-foreground">
                  {/* Folder kind and profile id are usually the same word, which
                      rendered as "orgchart · orgchart". Only show the pair when
                      it actually says two different things. */}
                  {candidate.targetFolderKind === candidate.targetProfileId
                    ? candidate.targetFolderKind
                    : `${candidate.targetFolderKind} · ${candidate.targetProfileId}`}
                </div>
              </button>
            );
          })}
        </div>
        {resolution.requiresUserConfirmation ? (
          <div
            className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs leading-snug text-amber-900"
            data-testid="tid.compass.sourceIntentReview.required"
          >
            {selectedIntent === null
              ? 'Choose the closest data type before reviewing field mappings.'
              : `Matched to ${selectedCandidate.label}, but another type scored close behind. Check this is right before importing.`}
          </div>
        ) : null}
      </section>

      <section className="min-h-0 rounded-lg border border-border bg-background">
        <div className="border-b border-border p-3">
          {/* No status pills here: they restated the mapped / needs-attention /
              ignored counts that the filter buttons below already carry, and
              the filters are the actionable copy of the same numbers. */}
          <div>
            <div className="text-sm font-semibold text-foreground">Map fields</div>
            <div className="text-xs text-muted-foreground">
              {mappingRows.length} Atlas field{mappingRows.length === 1 ? '' : 's'} matched against{' '}
              {columnCount.toLocaleString()} column{columnCount === 1 ? '' : 's'} in your file. Correct anything that needs attention.
            </div>
          </div>

          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative min-w-0 flex-1 sm:max-w-md">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 size-3.5 text-muted-foreground" />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search Atlas fields, source columns, or examples"
                className="h-8 pl-8 text-xs"
                data-testid="tid.compass.sourceIntentReview.search"
              />
            </div>
            <div className="flex flex-wrap gap-1" role="group" aria-label="Mapping filters">
              {([
                ['all', `All rows ${mappingRows.length + selectedCandidate.unrecognizedHeaders.length}`],
                ['attention', `Needs attention ${attentionCount}`],
                ['mapped', `Mapped ${mappedCount}`],
                ['ignored', `Ignored ${ignoredHeaders.length}`],
              ] as const).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setFilter(value)}
                  className={cn(
                    'rounded-md border px-2 py-1 text-[11px] font-medium',
                    filter === value
                      ? 'border-primary bg-[color:var(--primary)]/10 text-primary'
                      : 'border-border bg-background text-muted-foreground hover:text-foreground',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div
          ref={mappingViewportRef}
          className="max-h-[min(52vh,520px)] min-h-48 overflow-y-auto overscroll-contain"
          data-testid="tid.compass.sourceIntentReview.mapping"
        >
          <div className="sticky top-0 z-10 hidden grid-cols-[minmax(180px,1fr)_minmax(220px,0.9fr)_minmax(180px,1fr)_100px] gap-3 border-b border-border bg-muted/95 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.05em] text-muted-foreground backdrop-blur md:grid">
            <div>Atlas field</div>
            <div>Source column</div>
            <div>Examples</div>
            <div>Status</div>
          </div>

          {visibleMappingRows.map((row) => {
            const selectedSource = mappingSelections[row.id] ?? null;
            const examples = examplesFor(sampleRows, selectedSource);
            const missing = row.required && !selectedSource;
            return (
              <div
                key={row.id}
                className="grid gap-2 border-b border-border/70 px-3 py-2.5 last:border-b-0 md:grid-cols-[minmax(180px,1fr)_minmax(220px,0.9fr)_minmax(180px,1fr)_100px] md:items-center md:gap-3"
                data-testid={`tid.compass.sourceIntentReview.field.${row.id}`}
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-foreground" title={row.label}>
                    {row.label}
                    {row.required ? <span className="ml-1 text-red-600">*</span> : null}
                    {assistedRoleIds?.includes(row.id) && selectedSource ? (
                      <span
                        className="ml-1.5 rounded bg-muted px-1 py-0.5 align-middle text-[9px] font-medium uppercase tracking-wide text-muted-foreground"
                        title="Suggested from the column name and its values — check before importing"
                        data-testid={`tid.compass.sourceIntentReview.suggested.${row.id}`}
                      >
                        Suggested
                      </span>
                    ) : null}
                  </div>
                  <div className="truncate font-mono text-[10px] text-muted-foreground">
                    {row.kind === 'registered' ? row.id.replace(/^registered:/, '') : row.id}
                  </div>
                </div>
                <Combobox
                  options={headerOptions}
                  value={selectedSource ?? UNMAPPED_VALUE}
                  onValueChange={(value) =>
                    onMappingChange(row.id, value === UNMAPPED_VALUE ? null : value)
                  }
                  placeholder="Select source column"
                  searchPlaceholder="Search source columns..."
                  triggerClassName="h-8 justify-between px-2 text-xs"
                />
                <div className="min-w-0 truncate text-[11px] text-muted-foreground" title={examples.join(', ')}>
                  {examples.length > 0
                    ? examples.join(', ')
                    : selectedSource
                      ? 'No populated sample values'
                      : 'No source column selected'}
                </div>
                <MappingStatus missing={missing} mapped={Boolean(selectedSource)} />
              </div>
            );
          })}

          {visibleUnknownHeaders.map((header) => {
            const target = mappedUnknownTarget(header);
            return (
              <div
                key={`unknown:${header}`}
                className="grid gap-2 border-b border-amber-100 bg-amber-50/40 px-3 py-2.5 last:border-b-0 md:grid-cols-[minmax(180px,1fr)_minmax(220px,0.9fr)_minmax(180px,1fr)_100px] md:items-center md:gap-3"
                data-testid={`tid.compass.sourceIntentReview.unrecognized.${header}`}
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-foreground">
                    Choose an Atlas field
                  </div>
                  <div className="truncate text-[10px] text-amber-800">
                    Source column: {header}
                  </div>
                </div>
                <Combobox
                  options={targetOptions}
                  value={target ?? UNMAPPED_VALUE}
                  onValueChange={(value) => {
                    if (target) {
                      onMappingChange(registeredFieldSelectionId(target), null);
                    }
                    if (value !== UNMAPPED_VALUE) {
                      onMappingChange(registeredFieldSelectionId(value), header);
                    }
                  }}
                  placeholder="Map to an Atlas field"
                  searchPlaceholder="Search Atlas fields..."
                  triggerClassName="h-8 justify-between px-2 text-xs"
                />
                <div className="min-w-0 truncate text-[11px] text-muted-foreground">
                  {examplesFor(sampleRows, header).join(', ') || 'No populated sample values'}
                </div>
                <MappingStatus missing={false} mapped={Boolean(target)} ignored={!target} />
              </div>
            );
          })}

          {visibleMappingRows.length === 0 && visibleUnknownHeaders.length === 0 ? (
            <div className="flex min-h-40 items-center justify-center px-4 text-sm text-muted-foreground">
              No fields match this search and filter.
            </div>
          ) : null}
        </div>

        {missingRequired.length > 0 ? (
          <div
            className="border-t border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900"
            aria-live="polite"
          >
            Required mappings still missing: {missingRequired.map((field) => field.label).join(', ')}
          </div>
        ) : null}
      </section>
    </div>
  );
};

function examplesFor(
  sampleRows: readonly Record<string, unknown>[],
  sourceField: string | null,
): string[] {
  if (!sourceField) return [];
  return Array.from(new Set(
    sampleRows
      .map((row) => row[sourceField])
      .filter((value) => value != null && String(value).trim().length > 0)
      .map((value) => String(value).trim()),
  )).slice(0, 3);
}

function matchesQuery(query: string, values: readonly (string | null)[]): boolean {
  if (!query) return true;
  return values.some((value) => value?.toLowerCase().includes(query));
}

/**
 * Fields filed on something other than the person say so, since the same
 * column name (FTE, location) means a different record on a seat-per-row
 * source than on a people directory.
 */
function registeredFieldLabel(
  targetField: string,
  entityRefs: Readonly<Record<string, string>>,
): string {
  const label = humanizeField(targetField);
  const entityRef = entityRefs[targetField];
  return entityRef && entityRef !== 'person'
    ? `${label} (${humanizeField(entityRef)})`
    : label;
}

function humanizeField(field: string): string {
  return field
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function MappingStatus({
  missing,
  mapped,
  ignored = false,
}: {
  missing: boolean;
  mapped: boolean;
  ignored?: boolean;
}): React.ReactElement {
  if (missing) {
    return <span className="text-[11px] font-medium text-amber-700">Required</span>;
  }
  if (mapped) {
    return <span className="text-[11px] font-medium text-emerald-700">Mapped</span>;
  }
  return <span className="text-[11px] text-muted-foreground">{ignored ? 'Ignored' : 'Optional'}</span>;
}

